import "./_store-locator.scss";
import "simplebar";
import Choices from "choices.js";
import * as bootstrap from "bootstrap";

/**
 * Store Locator with Google Maps and Clustering
 */
class StoreLocator {
  constructor(element) {
    this.element = element;
    this.config = JSON.parse(element.dataset.storeLocator);
    this.map = null;
    this.markers = [];
    this.markerCluster = null;
    this.infoWindow = null;
    this.stores = [];
    this.tipo_vista = this.config.tipo_vista;
    this.isMapLoaded = false;
    this.infoWindowTemplate = null;
    this.userLocation = null; // Store user's geolocation
    this.simplebarInstance = null; // Store Simplebar instance
    this.userMarker = null; // Store user location marker
    this.choicesInstances = []; // Store Choices.js instances

    // Pagination state
    this.currentPage = 1;
    this.totalPages = 1;
    this.isLoading = false;
    this.hasMore = false;

    this.init();
  }

  init() {
    console.log("[Store Locator] Inizializzazione...", {
      config: this.config,
      tipo_vista: this.tipo_vista,
      ajax_url: window.siteVars?.ajaxUrl || this.config.ajax_url,
    });

    // Load InfoWindow template
    this.loadInfoWindowTemplate();

    // Wait for next tick to ensure form is in DOM
    setTimeout(() => {
      // Parse URL parameters and pre-fill form
      this.parseURLParameters();

      // Initialize Choices.js AFTER parsing URL parameters
      // This ensures selected values are properly set before Choices.js initializes
      this.initChoices();
    }, 100);

    // Load Google Maps API
    this.loadGoogleMapsAPI()
      .then(() => {
        console.log("[Store Locator] Google Maps API caricata");
        // Init map only if visible (not hidden on mobile for twocol view)
        this.initMapIfVisible();

        // Load stores with URL filters if present
        const urlFilters = this.getFiltersFromURL();
        this.loadStores(urlFilters);

        this.bindEvents();
      })
      .catch((error) => {
        console.error("[Store Locator] Errore caricamento Google Maps:", error);
        this.showError("Errore nel caricamento di Google Maps");
      });
  }
  /**
   * Parse URL parameters and pre-fill form fields
   */
  parseURLParameters() {
    const urlParams = new URLSearchParams(window.location.search);
    const filterForm = this.element.querySelector("#store-locator-filter");
    if (!filterForm) return;

    console.log("[Store Locator] Parsing URL parameters...");

    // Search parameter
    const search = urlParams.get("search");
    if (search) {
      const searchInput = filterForm.querySelector('input[name="search"]');
      if (searchInput) {
        searchInput.value = search;
        console.log("[Store Locator] Pre-filled search:", search);
      }
    }

    // Open now parameter
    const openNow = urlParams.get("open_now");
    if (openNow === "1" || openNow === "true") {
      const openNowCheckbox = filterForm.querySelector(
        'input[name="open_now"]',
      );
      if (openNowCheckbox) {
        openNowCheckbox.checked = true;
        console.log("[Store Locator] Pre-filled open_now: true");
      }
    }

    // Distance parameters (geolocation)
    const userLat = urlParams.get("user_lat");
    const userLng = urlParams.get("user_lng");
    const distance = urlParams.get("distance");

    if (userLat && userLng) {
      const latInput = filterForm.querySelector('input[name="user_lat"]');
      const lngInput = filterForm.querySelector('input[name="user_lng"]');

      if (latInput) latInput.value = userLat;
      if (lngInput) lngInput.value = userLng;

      this.userLocation = {
        lat: parseFloat(userLat),
        lng: parseFloat(userLng),
      };

      // Show geo controls
      const geoRangeWrapper = this.element.querySelector("#geo-range-wrapper");
      const geoClearBtn = this.element.querySelector("#filter-geo-clear");
      const geoBtn = this.element.querySelector("#filter-geolocation");

      if (geoRangeWrapper) geoRangeWrapper.style.display = "block";
      if (geoClearBtn) geoClearBtn.style.display = "inline-block";
      if (geoBtn) {
        geoBtn.disabled = true;
        geoBtn.innerHTML = '<i class="icon-check me-2"></i>Posizione attiva';
        geoBtn.classList.remove("btn-outline-primary");
        geoBtn.classList.add("btn-success");
      }

      console.log("[Store Locator] Pre-filled geolocation:", {
        lat: userLat,
        lng: userLng,
      });
    }

    if (distance) {
      const distanceInput = filterForm.querySelector('input[name="distance"]');
      const distanceValue = this.element.querySelector("#distance-value");

      if (distanceInput) distanceInput.value = distance;
      if (distanceValue) distanceValue.textContent = distance;

      console.log("[Store Locator] Pre-filled distance:", distance);
    }

    // Taxonomy parameters (supports multiple values)
    // Format: taxonomy[tax_name][]=term_id&taxonomy[tax_name][]=term_id2
    // OR: taxonomy[tax_name][]=12,18 (comma-separated)
    urlParams.forEach((value, key) => {
      const taxonomyMatch = key.match(/^taxonomy\[([^\]]+)\]\[\]$/);
      if (taxonomyMatch) {
        const taxName = taxonomyMatch[1];
        const select = filterForm.querySelector(
          `select[name="taxonomy[${taxName}][]"]`,
        );

        console.log(
          "[Store Locator] Looking for select with name:",
          `taxonomy[${taxName}][]`,
          "found:",
          !!select,
        );

        if (select && select.multiple) {
          // Split by comma if multiple values in one parameter
          const values = value.includes(",") ? value.split(",") : [value];

          values.forEach((val) => {
            val = val.trim();
            // Find option and mark as selected
            const option = select.querySelector(`option[value="${val}"]`);
            console.log(
              "[Store Locator] Looking for option with value:",
              val,
              "found:",
              !!option,
            );

            if (option) {
              option.selected = true;
              console.log(
                "[Store Locator] Pre-selected taxonomy option:",
                taxName,
                val,
              );
            }
          });
        }
      }
    });

    // Toggle parameters
    // Format: toggle[toggle_name]=cpt_slug
    urlParams.forEach((value, key) => {
      const toggleMatch = key.match(/^toggle\[([^\]]+)\]$/);
      if (toggleMatch) {
        const toggleName = toggleMatch[1];
        const checkbox = filterForm.querySelector(
          `input[name="toggle[${toggleName}]"][value="${value}"]`,
        );

        if (checkbox) {
          checkbox.checked = true;
          console.log("[Store Locator] Pre-checked toggle:", toggleName, value);
        }
      }
    });
  }

  /**
   * Get filters from URL parameters
   */
  getFiltersFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const filters = {
      search: "",
      taxonomy: {},
      toggle: {},
      open_now: "",
      user_lat: "",
      user_lng: "",
      distance: "",
    };

    // Search
    filters.search = urlParams.get("search") || "";

    // Open now
    const openNow = urlParams.get("open_now");
    if (openNow === "1" || openNow === "true") {
      filters.open_now = "1";
    }

    // Geolocation
    filters.user_lat = urlParams.get("user_lat") || "";
    filters.user_lng = urlParams.get("user_lng") || "";
    filters.distance = urlParams.get("distance") || "";

    // Taxonomy (supports multiple values)
    // Supports both formats:
    // - taxonomy[name][]=12&taxonomy[name][]=18
    // - taxonomy[name][]=12,18
    urlParams.forEach((value, key) => {
      const taxonomyMatch = key.match(/^taxonomy\[([^\]]+)\]\[\]$/);
      if (taxonomyMatch) {
        const taxName = taxonomyMatch[1];
        if (!filters.taxonomy[taxName]) {
          filters.taxonomy[taxName] = [];
        }

        // Split by comma if multiple values in one parameter
        const values = value.includes(",") ? value.split(",") : [value];

        values.forEach((val) => {
          val = val.trim();
          if (val && val !== "" && val !== "Tutti") {
            filters.taxonomy[taxName].push(val);
          }
        });
      }
    });

    // Toggle
    urlParams.forEach((value, key) => {
      const toggleMatch = key.match(/^toggle\[([^\]]+)\]$/);
      if (toggleMatch) {
        const toggleName = toggleMatch[1];
        if (value && value !== "") {
          filters.toggle[toggleName] = value;
        }
      }
    });

    // Clean up empty arrays
    Object.keys(filters.taxonomy).forEach((taxName) => {
      if (filters.taxonomy[taxName].length === 0) {
        delete filters.taxonomy[taxName];
      }
    });

    console.log("[Store Locator] Filters from URL:", filters);
    // In modalita' autocomplete non usiamo mai il parametro search
    if (this.config.autocomplete) {
      filters.search = "";
    }
    return filters;
  }

  /**
   * Load InfoWindow template from DOM
   */
  loadInfoWindowTemplate() {
    const templateElement = document.getElementById(
      "store-infowindow-template",
    );
    if (templateElement) {
      this.infoWindowTemplate = templateElement.innerHTML.trim();
      console.log("[Store Locator] Template InfoWindow caricato");
    } else {
      console.warn(
        "[Store Locator] Template InfoWindow non trovato, uso template di default",
      );
      // Fallback template
      this.infoWindowTemplate = `
        <div class="store-info-window">
          <h6 class="mb-2">{{title}}</h6>
          {{#address}}<p class="mb-1 small"><i class="icon-map-pin me-1"></i>{{address}}</p>{{/address}}
          {{#phone}}<p class="mb-1 small"><i class="icon-phone me-1"></i><a href="tel:{{phone}}">{{phone}}</a></p>{{/phone}}
          {{#email}}<p class="mb-1 small"><i class="icon-envelope me-1"></i><a href="mailto:{{email}}">{{email}}</a></p>{{/email}}
          {{#website}}<p class="mb-1 small"><i class="icon-globe me-1"></i><a href="{{website}}" target="_blank">{{website}}</a></p>{{/website}}
          <a href="{{permalink}}" class="btn btn-sm btn-primary mt-2">Dettagli</a>
        </div>
      `;
    }
  }

  /**
   * Load Google Maps API with Clustering
   */
  loadGoogleMapsAPI() {
    return new Promise((resolve, reject) => {
      if (typeof google !== "undefined" && google.maps) {
        resolve();
        return;
      }

      window.initStoreLocatorMap = () => {
        resolve();
      };

      const script = document.createElement("script");
      // Carico sia marker che places per supportare Autocomplete quando abilitato
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.config.api_key}&callback=initStoreLocatorMap&libraries=marker,places&language=it`;
      script.async = true;
      script.defer = true;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * Initialize Google Map
   */
  initMap() {
    const mapElement = this.element.querySelector("#store-locator-map");
    if (!mapElement) return;

    // Determine initial center from config or use default (Roma)
    let centerLat = 41.9028;
    let centerLng = 12.4964;

    if (this.config.center_lat && this.config.center_lng) {
      centerLat = parseFloat(this.config.center_lat);
      centerLng = parseFloat(this.config.center_lng);
      console.log("[Store Locator] Centro mappa personalizzato:", {
        lat: centerLat,
        lng: centerLng,
      });
    }

    // Determine initial zoom from config or use default
    const initialZoom = this.config.zoom ? parseInt(this.config.zoom) : 6;
    console.log("[Store Locator] Zoom iniziale:", initialZoom);

    // Map options
    const mapOptions = {
      center: { lat: centerLat, lng: centerLng },
      zoom: initialZoom,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
    };

    // Apply custom styles from Snazzy Maps if available
    if (this.config.map_styles && Array.isArray(this.config.map_styles)) {
      mapOptions.styles = this.config.map_styles;
      console.log("[Store Locator] Stile mappa personalizzato applicato");
    }

    this.map = new google.maps.Map(mapElement, mapOptions);

    this.infoWindow = new google.maps.InfoWindow();
    this.isMapLoaded = true;
  }

  /**
   * Initialize map only if visible (handles mobile twocol view)
   */
  initMapIfVisible() {
    const mapElement = this.element.querySelector("#store-locator-map");
    if (!mapElement) return;

    // Check if map container is visible
    const isVisible = mapElement.offsetParent !== null;

    if (isVisible) {
      // Map is visible, init immediately
      console.log("[Store Locator] Map visible, initializing");
      this.initMap();
    } else {
      // Map is hidden (mobile twocol), delay init until shown
      console.log("[Store Locator] Map hidden, delaying initialization");
      // Map will be initialized when toggle button is clicked
    }
  }

  /**
   * Load stores via AJAX
   */
  loadStores(filters = {}) {
    this.showLoading();

    // Use window.siteVars.ajaxUrl if available, otherwise fallback
    let ajaxUrl = null;

    if (window.siteVars && window.siteVars.ajaxUrl) {
      ajaxUrl = window.siteVars.ajaxUrl;
      console.log("[Store Locator] Usando window.siteVars.ajaxUrl:", ajaxUrl);
    } else if (this.config.ajax_url) {
      ajaxUrl = this.config.ajax_url;
      console.warn("[Store Locator] Fallback a config.ajax_url:", ajaxUrl);
    } else {
      // Last resort fallback
      ajaxUrl = "/wp-admin/admin-ajax.php";
      console.error("[Store Locator] Usando URL hardcoded:", ajaxUrl);
    }

    console.log("[Store Locator] Caricamento stores...", {
      ajax_url: ajaxUrl,
      action: "get_store_locator_data",
      cpt: this.config.cpt,
      is_single_mode: this.config.is_single_mode,
      single_post_id: this.config.single_post_id,
      filters: filters,
      nonce: this.config.nonce,
    });

    const formData = new FormData();
    formData.append("action", "get_store_locator_data");
    formData.append("nonce", this.config.nonce);
    formData.append("cpt", this.config.cpt);
    formData.append("tipo_vista", this.config.tipo_vista);
    formData.append("tpl", this.config.tpl || "");

    // Add pagination params
    formData.append("page", this.currentPage);
    formData.append("per_page", this.config.per_page || 10);

    // Add single mode params
    if (this.config.is_single_mode) {
      formData.append("is_single_mode", "true");
      formData.append("single_post_id", this.config.single_post_id);
      console.log(
        "[Store Locator] Modalità single post attiva, carico solo post ID:",
        this.config.single_post_id,
      );
    }

    // Add field mapping if available
    if (this.config.field_mapping) {
      formData.append(
        "field_mapping",
        JSON.stringify(this.config.field_mapping),
      );
    }

    // Add current taxonomy if archive
    if (this.config.tax && this.config.term) {
      formData.append("tax", this.config.tax);
      formData.append("term", this.config.term);
    }

    // Add filters (solo se NON in single mode)
    if (!this.config.is_single_mode) {
      if (filters.search) {
        formData.append("search", filters.search);
      }
      if (filters.open_now) {
        formData.append("open_now", filters.open_now);
      }
      if (filters.user_lat && filters.user_lng && filters.distance) {
        formData.append("user_lat", filters.user_lat);
        formData.append("user_lng", filters.user_lng);
        formData.append("distance", filters.distance);
      }
      if (filters.taxonomy) {
        Object.keys(filters.taxonomy).forEach((tax) => {
          const values = filters.taxonomy[tax];
          // Handle both single value and array of values
          if (Array.isArray(values)) {
            values.forEach((value) => {
              if (value) {
                formData.append(`taxonomy[${tax}][]`, value);
              }
            });
          } else if (values) {
            formData.append(`taxonomy[${tax}]`, values);
          }
        });
      }
      if (filters.toggle) {
        Object.keys(filters.toggle).forEach((field) => {
          if (filters.toggle[field]) {
            formData.append(`toggle[${field}]`, filters.toggle[field]);
          }
        });
      }
    }

    fetch(ajaxUrl, {
      method: "POST",
      body: formData,
    })
      .then((response) => {
        console.log("[Store Locator] Response ricevuta:", response);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        console.log("[Store Locator] Dati ricevuti:", data);
        if (data.success) {
          const isFirstPage = this.currentPage === 1;

          // Update pagination state
          if (data.data.pagination) {
            this.totalPages = data.data.pagination.total_pages;
            this.hasMore = data.data.pagination.has_more;
            console.log("[Store Locator] Pagination:", {
              current: this.currentPage,
              total: this.totalPages,
              hasMore: this.hasMore,
            });
          }

          // Handle stores data
          if (isFirstPage) {
            // First page: replace stores
            this.stores = data.data.stores;
          } else {
            // Subsequent pages: append stores
            this.stores = [...this.stores, ...data.data.stores];
          }

          console.log("[Store Locator] Stores totali:", this.stores.length);

          // Render cards (append or replace based on page)
          this.renderCards(data.data.html, isFirstPage);

          // Update map only on first page (single mode) or always (other modes)
          if (isFirstPage || !data.data.is_single_mode) {
            this.updateMap();
          }

          // Show/hide load more button
          this.updateLoadMoreButton();

          // Update store count
          if (!data.data.is_single_mode) {
            const countStore = document.querySelector(".store-count");
            countStore.textContent = this.stores.length;
          }
        } else {
          console.error("[Store Locator] Errore dal server:", data.data);
          this.showError(data.data.message || "Errore nel caricamento");
          this.showDebugMessage(
            "✗ " + (data.data.message || "Errore dal server"),
            "danger",
          );
        }
      })
      .catch((error) => {
        console.error("[Store Locator] Errore AJAX:", error);
        console.error("[Store Locator] Stack trace:", error.stack);
        this.showError("Errore di connessione: " + error.message);
        this.showDebugMessage(
          "✗ Errore connessione: " + error.message,
          "danger",
        );
      })
      .finally(() => {
        this.hideLoading();
        this.isLoading = false;
      });
  }

  /**
   * Render cards in list
   */
  renderCards(html, replace = true) {
    const listContainer = this.element.querySelector("#store-locator-list");
    if (listContainer) {
      if (html.trim() === "") {
        // Load no results template
        this.loadNoResultsTemplate()
          .then((noResultsHtml) => {
            if (replace) {
              listContainer.innerHTML = noResultsHtml;
            } else {
              listContainer.insertAdjacentHTML("beforeend", noResultsHtml);
            }
            this.bindCardEvents();
          })
          .catch((error) => {
            console.error(
              "[Store Locator] Errore caricamento no results template:",
              error,
            );
            listContainer.innerHTML =
              '<div class="col-12"><div class="alert alert-warning">' +
              '<?php _e("Nessun risultato trovato", "tst"); ?>' +
              "</div></div>";
          });
      } else {
        if (replace) {
          // First page: replace content
          listContainer.innerHTML = html;
        } else {
          // Subsequent pages: append content
          listContainer.insertAdjacentHTML("beforeend", html);
        }

        // Re-bind card events
        this.bindCardEvents();
      }
    }
  }

  /**
   * Load no results template via AJAX
   */
  loadNoResultsTemplate() {
    return new Promise((resolve, reject) => {
      let ajaxUrl =
        window.siteVars && window.siteVars.ajaxUrl
          ? window.siteVars.ajaxUrl
          : this.config.ajax_url || "/wp-admin/admin-ajax.php";

      const formData = new FormData();
      formData.append("action", "get_no_results_template");
      formData.append("nonce", this.config.nonce);

      fetch(ajaxUrl, {
        method: "POST",
        body: formData,
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then((html) => {
          resolve(html);
        })
        .catch((error) => {
          console.error(
            "[Store Locator] Errore fetch no results template:",
            error,
          );
          reject(error);
        });
    });
  }

  /**
   * Update map with markers and clustering
   */
  updateMap() {
    if (!this.isMapLoaded) return;

    // Clear existing markers and clusters
    if (this.markerCluster) {
      this.markerCluster.clearMarkers();
    }
    this.markers = [];

    if (this.stores.length === 0) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();

    // Create markers
    this.stores.forEach((store) => {
      if (!store.lat || !store.lng) return;

      const position = {
        lat: parseFloat(store.lat),
        lng: parseFloat(store.lng),
      };

      const marker = new google.maps.Marker({
        position: position,
        map: this.map,
        title: store.title,
        icon: this.config.icon || null,
      });

      // Info window on click - DISABILITATO in single mode
      if (!this.config.is_single_mode) {
        marker.addListener("click", () => {
          this.showInfoWindow(marker, store);
          this.highlightCard(store.id);
        });
      }

      this.markers.push(marker);
      bounds.extend(position);
    });

    // Fit bounds to show all markers
    if (this.markers.length > 0) {
      this.map.fitBounds(bounds);

      // Don't zoom too much for single marker
      if (this.markers.length === 1) {
        const listener = google.maps.event.addListenerOnce(
          this.map,
          "bounds_changed",
          () => {
            if (this.map.getZoom() > 15) {
              this.map.setZoom(15);
            }
          },
        );
      }
    }

    // Initialize MarkerClusterer
    this.initClusterer();
  }

  /**
   * Initialize marker clustering
   */
  initClusterer() {
    // Prepare cluster options
    const clusterOptions = {
      map: this.map,
      markers: this.markers,
    };

    // Add custom cluster icon if available
    if (this.config.icon_cluster) {
      console.log(
        "[Store Locator] Using custom cluster icon:",
        this.config.icon_cluster,
      );

      // Custom renderer for cluster icon
      clusterOptions.renderer = {
        render: ({ count, position }) => {
          // Create custom marker for cluster
          return new google.maps.Marker({
            position,
            icon: {
              url: this.config.icon_cluster,
              scaledSize: new google.maps.Size(50, 50), // Adjust size as needed
            },
            label: {
              text: String(count),
              color: "#ffffff",
              fontSize: "14px",
              fontWeight: "bold",
            },
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
          });
        },
      };
    }

    // Using MarkerClusterer library (need to be included)
    if (
      typeof markerClusterer !== "undefined" &&
      markerClusterer.MarkerClusterer
    ) {
      this.markerCluster = new markerClusterer.MarkerClusterer(clusterOptions);
    } else {
      // Fallback: load from CDN
      const script = document.createElement("script");
      script.src =
        "https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js";
      script.onload = () => {
        this.markerCluster = new markerClusterer.MarkerClusterer(
          clusterOptions,
        );
      };
      document.head.appendChild(script);
    }
  }

  /**
   * Render template with data (simple Mustache-like template engine)
   */
  renderTemplate(template, data) {
    let rendered = template;

    // Replace simple variables {{key}}
    Object.keys(data).forEach((key) => {
      const value = data[key] || "";
      const regex = new RegExp(`{{${key}}}`, "g");
      rendered = rendered.replace(regex, value);
    });

    // Handle conditional sections {{#key}}...{{/key}}
    Object.keys(data).forEach((key) => {
      const value = data[key];
      const sectionRegex = new RegExp(
        `{{#${key}}}([\\s\\S]*?){{/${key}}}`,
        "g",
      );

      if (value && value !== "" && value !== null && value !== undefined) {
        // Show section, remove markers
        rendered = rendered.replace(sectionRegex, "$1");
      } else {
        // Remove entire section
        rendered = rendered.replace(sectionRegex, "");
      }
    });

    return rendered;
  }

  /**
   * Show info window for marker
   */
  showInfoWindow(marker, store) {
    // Render template with store data
    const content = this.renderTemplate(this.infoWindowTemplate, {
      id: store.id,
      title: store.title,
      address: store.address,
      phone: store.phone,
      email: store.email,
      website: store.website,
      permalink: store.permalink,
      hours_status: store.hours_status, // Aggiungi hours_status
    });

    this.infoWindow.setContent(content);
    this.infoWindow.open(this.map, marker);
  }

  /**
   * Highlight card in list
   */
  highlightCard(storeId) {
    // Remove previous highlights
    this.element.querySelectorAll(".store-locator-card").forEach((card) => {
      card.classList.remove("active", "highlight");
    });

    // Highlight selected card
    const card = this.element.querySelector(`#store-card-${storeId}`);
    if (card) {
      card.classList.add("active", "highlight");

      // Scroll to card if in twocol view
      if (this.tipo_vista === "twocol") {
        card.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }

  resetChoicesSelections() {
    // Reset Choices.js instances
    this.choicesInstances.forEach((instance) => {
      console.log(instance, instance);

      if (instance && typeof instance.removeActiveItems === "function") {
        instance.removeActiveItems();
      }
    });
  }
  /**
   * Bind events
   */
  bindEvents() {
    // Filter form submit
    const filterForm = this.element.querySelector("#store-locator-filter");
    if (filterForm) {
      filterForm.addEventListener("submit", (e) => {
        e.preventDefault();
        this.resetPagination();
        this.handleFilterSubmit(e.target);
      });

      // Reset button
      const resetBtn = this.element.querySelector("#filter-reset");
      if (resetBtn) {
        resetBtn.addEventListener("click", () => {
          filterForm.reset();

          this.resetChoicesSelections();

          // Reset geolocation state and related UI
          this.userLocation = null;
          const latInput = this.element.querySelector("#filter-user-lat");
          const lngInput = this.element.querySelector("#filter-user-lng");
          if (latInput) latInput.value = "";
          if (lngInput) lngInput.value = "";
          const geoRangeWrapper =
            this.element.querySelector("#geo-range-wrapper");
          if (geoRangeWrapper) geoRangeWrapper.style.display = "none";
          const geoClearBtn = this.element.querySelector("#filter-geo-clear");
          if (geoClearBtn) geoClearBtn.style.display = "none";
          const geoBtn = this.element.querySelector("#filter-geolocation");
          if (geoBtn) {
            geoBtn.disabled = false;
            geoBtn.innerHTML =
              '<i class="icon-navigation me-2"></i>Usa la mia posizione';
            geoBtn.classList.remove("btn-success");
            geoBtn.classList.add("btn-outline-light");
          }
          this.showGeoStatus("", "");
          this.removeUserLocationMarker();

          // Clear URL parameters
          this.clearURLParameters();

          this.resetPagination();
          this.loadStores();
        });
      }

      // Auto-submit on input/select change
      this.bindFilterAutoSubmit(filterForm);
    }

    // Toggle view buttons (single mode and twocol on mobile)
    if (this.tipo_vista === "single" || this.tipo_vista === "twocol") {
      const toggleButtons = this.element.querySelectorAll("[data-toggle-view]");
      toggleButtons.forEach((btn) => {
        btn.addEventListener("click", () => {
          this.toggleView(btn.dataset.toggleView);
        });
      });
    }

    // Bind card events
    this.bindCardEvents();

    // Bind geolocation events
    this.bindGeolocationEvents();

    // Bind load more events
    this.bindLoadMoreEvents();

    // Inizializza Google Places Autocomplete se richiesto da config
    if (this.config.autocomplete) {
      this.initPlaceAutocomplete();
    }
  }

  /**
   * Inizializza Google Places Autocomplete su #filter-search
   * - Attiva ricerca alla selezione del place
   * - Popola hidden user_lat/user_lng e NON invia il parametro search
   */
  initPlaceAutocomplete() {
    const input = this.element.querySelector("#filter-search");
    if (!input) return;

    // Se la libreria places non è disponibile, fallback silenzioso
    if (!window.google || !google.maps || !google.maps.places) {
      console.warn(
        "[Store Locator] Places non disponibile, fallback a ricerca testuale",
      );
      return;
    }

    try {
      const options = {
        componentRestrictions: { country: ["it", "si"] },
      };

      const autocomplete = new google.maps.places.Autocomplete(input, options);

      // Limita i campi richiesti
      if (typeof autocomplete.setFields === "function") {
        autocomplete.setFields(["geometry", "formatted_address", "name"]);
      }

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (!place || !place.geometry || !place.geometry.location) {
          console.warn(
            "[Store Locator] Nessuna geometria dal place selezionato",
          );
          return;
        }

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        // Aggiorna hidden inputs per filtro distanza
        const latInput = this.element.querySelector("#filter-user-lat");
        const lngInput = this.element.querySelector("#filter-user-lng");
        const distanceInput = this.element.querySelector("#filter-distance");
        if (latInput) latInput.value = lat;
        if (lngInput) lngInput.value = lng;
        // Se non impostata, mantieni il default presente nell'HTML (es. 50)

        // Aggiorna stato interno e marker utente
        this.userLocation = { lat, lng };
        this.addUserLocationMarker();

        // UI geolocalizzazione: mostra pulsante clear se presente
        const geoClearBtn = this.element.querySelector("#filter-geo-clear");
        if (geoClearBtn) geoClearBtn.style.display = "block";

        // Reset della select regioni (alternativa alla ricerca localizzata)
        const regionsSelect = this.element.querySelector(
          "#filter-tst_clinics_regions",
        );
        if (regionsSelect) this.clearChoicesSelection(regionsSelect);

        // Avvia ricerca usando solo coordinate (senza parametro search)
        const filterForm = this.element.querySelector("#store-locator-filter");
        this.resetPagination();
        if (filterForm) {
          this.handleFilterSubmit(filterForm);
        }
      });

      console.log("[Store Locator] Google Places Autocomplete inizializzato");
    } catch (e) {
      console.error("[Store Locator] Errore init Autocomplete:", e);
    }
  }

  /**
   * Initialize Choices.js for select elements
   */
  initChoices() {
    // Destroy existing instances
    this.choicesInstances.forEach((instance) => {
      if (instance && typeof instance.destroy === "function") {
        instance.destroy();
      }
    });
    this.choicesInstances = [];

    // Find all select2 elements in filter form
    const selects = this.element.querySelectorAll(
      ".store-locator-filter-form .select2",
    );

    console.log(
      "[Store Locator] Initializing Choices.js for",
      selects.length,
      "select elements",
    );

    selects.forEach((select) => {
      // Log selected options before Choices.js init
      const selectedOptions = Array.from(select.selectedOptions).map(
        (opt) => opt.value,
      );
      console.log(
        "[Store Locator] Select",
        select.name,
        "has pre-selected values:",
        selectedOptions,
      );

      const instance = new Choices(select, {
        allowHTML: true,
        editItems: true,
        maxItemCount: 5,
        shouldSort: false,
        removeItems: true,
        removeItemButtonAlignLeft: true,
        removeItemButton: true,
        searchEnabled: true,
        searchPlaceholderValue: "Cerca...",
        noChoicesText: "Nessuna opzione disponibile",
        noResultsText: "Nessun risultato trovato",
        itemSelectText: "",
      });

      this.choicesInstances.push(instance);
    });

    console.log(
      "[Store Locator] Choices.js initialized:",
      this.choicesInstances.length,
      "instances",
    );
  }

  /**
   * Bind auto-submit on filter changes
   */
  bindFilterAutoSubmit(filterForm) {
    if (!filterForm) return;

    // Get all input and select elements
    // Escludo i campi di ricerca interni di Choices: considero solo il campo principale #filter-search
    const mainSearchInput = filterForm.querySelector("#filter-search");
    const inputs = mainSearchInput ? [mainSearchInput] : [];
    const selects = filterForm.querySelectorAll("select");
    const checkboxes = filterForm.querySelectorAll(
      'input[type="checkbox"]:not(#filter-geolocation)',
    );
    const radios = filterForm.querySelectorAll('input[type="radio"]');

    // Debounce timeout for text inputs
    let textInputTimeout;

    // Text inputs - debounced (500ms)
    inputs.forEach((input) => {
      // Se è attiva l'autocomplete, non auto-submit su #filter-search
      if (this.config.autocomplete && input.id === "filter-search") {
        return; // gestito da Google Places place_changed
      }
      input.addEventListener("input", (e) => {
        clearTimeout(textInputTimeout);
        textInputTimeout = setTimeout(() => {
          console.log("[Store Locator] Text input changed, auto-submitting...");
          this.resetPagination();
          this.handleFilterSubmit(filterForm);
        }, 500);
      });
    });

    // Select dropdowns - immediate
    selects.forEach((select) => {
      select.addEventListener("change", () => {
        console.log("[Store Locator] Select changed, auto-submitting...");
        this.resetPagination();
        this.handleFilterSubmit(filterForm);
      });
    });

    // Checkboxes - immediate
    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", () => {
        console.log("[Store Locator] Checkbox changed, auto-submitting...");
        this.resetPagination();
        this.handleFilterSubmit(filterForm);
      });
    });

    // Radio buttons - immediate
    radios.forEach((radio) => {
      radio.addEventListener("change", () => {
        console.log("[Store Locator] Radio changed, auto-submitting...");
        this.resetPagination();
        this.handleFilterSubmit(filterForm);
      });
    });

    console.log("[Store Locator] Auto-submit bound to:", {
      inputs: inputs.length, // solo #filter-search
      selects: selects.length,
      checkboxes: checkboxes.length,
      radios: radios.length,
    });
  }

  /**
   * Pulisce la selezione di una select gestita da Choices e aggiorna la UI
   */
  clearChoicesSelection(selectEl) {
    if (!selectEl) return;

    console.log("Pulisce la selezione di una select", selectEl);

    // Deseleziona sul DOM
    Array.from(selectEl.options).forEach((opt) => (opt.selected = false));

    // Reset Choices.js instances
    this.resetChoicesSelections();

    // Notifica change ai listener
    selectEl.dispatchEvent(new Event("change", { bubbles: true }));
  }

  /**
   * Bind geolocation events
   */
  bindGeolocationEvents() {
    const geoBtn = this.element.querySelector("#filter-geolocation");
    const geoClearBtn = this.element.querySelector("#filter-geo-clear");
    const distanceRange = this.element.querySelector("#filter-distance");
    const distanceValue = this.element.querySelector("#distance-value");
    const filterForm = this.element.querySelector("#store-locator-filter");

    if (geoBtn) {
      geoBtn.addEventListener("click", () => {
        this.requestGeolocation();
      });
    }

    if (geoClearBtn) {
      geoClearBtn.addEventListener("click", () => {
        this.clearGeolocation();
      });
    }

    if (distanceRange && distanceValue) {
      distanceRange.addEventListener("input", (e) => {
        distanceValue.textContent = e.target.value;
      });

      // Auto-reload quando cambia il range (debounced)
      let rangeTimeout;
      distanceRange.addEventListener("change", () => {
        clearTimeout(rangeTimeout);
        rangeTimeout = setTimeout(() => {
          if (filterForm) {
            this.handleFilterSubmit(filterForm);
          }
        }, 300);
      });
    }
  }

  /**
   * Request user's geolocation
   */
  requestGeolocation() {
    const geoBtn = this.element.querySelector("#filter-geolocation");
    const geoStatus = this.element.querySelector("#geo-status");
    const geoRangeWrapper = this.element.querySelector("#geo-range-wrapper");
    const geoClearBtn = this.element.querySelector("#filter-geo-clear");

    if (!navigator.geolocation) {
      this.showGeoStatus(
        "Il tuo browser non supporta la geolocalizzazione.",
        "danger",
      );
      return;
    }

    // Update button state
    if (geoBtn) {
      geoBtn.disabled = true;
      geoBtn.innerHTML =
        '<span class="spinner-border spinner-border-sm me-2"></span>Rilevamento...';
    }

    this.showGeoStatus("Rilevamento posizione in corso...", "info");

    navigator.geolocation.getCurrentPosition(
      (position) => {
        // Success
        this.userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        console.log(
          "[Store Locator] Posizione utente rilevata:",
          this.userLocation,
        );

        // Update hidden inputs
        const latInput = this.element.querySelector("#filter-user-lat");
        const lngInput = this.element.querySelector("#filter-user-lng");

        if (latInput) latInput.value = this.userLocation.lat;
        if (lngInput) lngInput.value = this.userLocation.lng;

        // Show range slider
        if (geoRangeWrapper) geoRangeWrapper.style.display = "block";
        if (geoClearBtn) geoClearBtn.style.display = "block";

        // Update button state
        if (geoBtn) {
          geoBtn.disabled = true;
          geoBtn.innerHTML =
            '<i class="icon-check me-2"></i>Posizione rilevata';
          geoBtn.classList.remove("btn-outline-light");
          geoBtn.classList.add("btn-success");
        }

        this.showGeoStatus("✓ Posizione rilevata con successo", "success");

        // Reset della select regioni (alternativa alla ricerca localizzata)
        const regionsSelect = this.element.querySelector(
          "#filter-tst_clinics_regions",
        );
        if (regionsSelect) this.clearChoicesSelection(regionsSelect);

        // Add marker for user location
        this.addUserLocationMarker();

        // Auto-submit form to filter stores
        const filterForm = this.element.querySelector("#store-locator-filter");
        if (filterForm) {
          this.handleFilterSubmit(filterForm);
        }
      },
      (error) => {
        // Error
        console.error("[Store Locator] Errore geolocalizzazione:", error);

        let errorMessage = "Errore nel rilevamento della posizione.";

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage =
              "Permesso negato. Abilita la geolocalizzazione nelle impostazioni del browser.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Posizione non disponibile.";
            break;
          case error.TIMEOUT:
            errorMessage = "Timeout: impossibile rilevare la posizione.";
            break;
        }

        this.showGeoStatus(errorMessage, "danger");

        // Reset button
        if (geoBtn) {
          geoBtn.disabled = false;
          geoBtn.innerHTML =
            '<i class="icon-navigation me-2"></i>Usa la mia posizione';
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  }

  /**
   * Clear geolocation filter
   */
  clearGeolocation() {
    this.userLocation = null;

    // Clear hidden inputs
    const latInput = this.element.querySelector("#filter-user-lat");
    const lngInput = this.element.querySelector("#filter-user-lng");

    if (latInput) latInput.value = "";
    if (lngInput) lngInput.value = "";

    // Hide range slider
    const geoRangeWrapper = this.element.querySelector("#geo-range-wrapper");
    const geoClearBtn = this.element.querySelector("#filter-geo-clear");

    if (geoRangeWrapper) geoRangeWrapper.style.display = "none";
    if (geoClearBtn) geoClearBtn.style.display = "none";

    // Reset button
    const geoBtn = this.element.querySelector("#filter-geolocation");
    if (geoBtn) {
      geoBtn.disabled = false;
      geoBtn.innerHTML =
        '<i class="icon-navigation me-2"></i>Usa la mia posizione';
      geoBtn.classList.remove("btn-success");
      geoBtn.classList.add("btn-outline-light");
    }

    // Remove user location marker
    this.removeUserLocationMarker();

    // Clear status
    this.showGeoStatus("", "");

    // Reset pagination and reload without geo filter
    this.resetPagination();
    const filterForm = this.element.querySelector("#store-locator-filter");
    if (filterForm) {
      this.handleFilterSubmit(filterForm);
    }
  }

  /**
   * Add marker for user's location
   */
  addUserLocationMarker() {
    if (!this.isMapLoaded || !this.userLocation) return;

    // Remove existing user marker if any
    this.removeUserLocationMarker();

    // Create user location marker with custom icon
    this.userMarker = new google.maps.Marker({
      position: { lat: this.userLocation.lat, lng: this.userLocation.lng },
      map: this.map,
      title: "La tua posizione",
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: "#4285F4",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 2,
      },
      zIndex: 1000,
    });

    // Center map on user location
    this.map.setCenter(this.userLocation);
    this.map.setZoom(12);
  }

  /**
   * Remove user location marker
   */
  removeUserLocationMarker() {
    if (this.userMarker) {
      this.userMarker.setMap(null);
      this.userMarker = null;
    }
  }

  /**
   * Show geolocation status message
   */
  showGeoStatus(message, type) {
    const geoStatus = this.element.querySelector("#geo-status");
    if (geoStatus) {
      geoStatus.textContent = message;
      geoStatus.className = `form-text text-${type}`;
    }
  }

  /**
   * Bind card-specific events
   */
  bindCardEvents() {
    // (re)crea tooltip per gli elementi presenti nel container
    const cards = document.getElementById("store-locator-list");
    if (cards) {
      cards.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
        new bootstrap.Tooltip(el, { container: "body" });
      });
      console.log("[Store Locator] Bootstrap tooltips initialized");
    }
    // Show on map buttons
    const showOnMapBtns = this.element.querySelectorAll(".store-show-on-map");
    showOnMapBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        const storeId = btn.dataset.storeId;
        const lat = parseFloat(btn.dataset.lat);
        const lng = parseFloat(btn.dataset.lng);

        this.focusOnStore(storeId, lat, lng);
      });
    });

    // Initialize or update Simplebar for twocol view
    const simplebarWrapper = this.element.querySelector(
      ".store-locator-list-wrapper",
    );
    if (simplebarWrapper && this.tipo_vista === "twocol") {
      // Check if already initialized
      if (this.simplebarInstance) {
        console.log("[Store Locator] Simplebar recalculate");
        this.simplebarInstance.recalculate();
      } else {
        // First initialization
        console.log("[Store Locator] Simplebar init");
        // Use dynamic import to avoid compilation issues
        import("simplebar")
          .then((module) => {
            const SimpleBar = module.default;
            this.simplebarInstance = new SimpleBar(simplebarWrapper, {
              autoHide: false,
              scrollbarMinSize: 25,
            });
            console.log("[Store Locator] Simplebar initialized");
          })
          .catch((error) => {
            console.error("[Store Locator] Error loading Simplebar:", error);
          });
      }
    }
  } // close bind card events

  /**
   * Focus on specific store on map
   */
  focusOnStore(storeId, lat, lng) {
    if (!this.isMapLoaded) return;

    // Switch to map view if in single mode
    if (this.tipo_vista === "single") {
      this.toggleView("map");
    }

    // Center map on store
    this.map.setCenter({ lat, lng });
    this.map.setZoom(15);

    // Find and trigger marker click
    const marker = this.markers.find((m) => {
      return m.getPosition().lat() === lat && m.getPosition().lng() === lng;
    });

    if (marker) {
      const store = this.stores.find((s) => s.id == storeId);
      if (store) {
        this.showInfoWindow(marker, store);
      }
    }
  }

  /**
   * Handle filter form submit
   */
  handleFilterSubmit(form) {
    const formData = new FormData(form);
    const filters = {
      search: formData.get("search") || "",
      open_now: formData.get("open_now") || "",
      user_lat: formData.get("user_lat") || "",
      user_lng: formData.get("user_lng") || "",
      distance: formData.get("distance") || "",
      taxonomy: {},
      toggle: {},
    };

    // Get taxonomy filters (now supports multiple values)
    for (let [key, value] of formData.entries()) {
      if (key.startsWith("taxonomy[") && value) {
        // Extract taxonomy name from key (e.g., "taxonomy[category][]" -> "category")
        const match = key.match(/taxonomy\[(.*?)\]/);
        if (match) {
          const taxName = match[1];

          // Initialize array if not exists
          if (!filters.taxonomy[taxName]) {
            filters.taxonomy[taxName] = [];
          }

          // Add value to array (skip empty values)
          if (value !== "" && value !== "Tutti") {
            filters.taxonomy[taxName].push(value);
          }
        }
      }
      if (key.startsWith("toggle[") && value) {
        const fieldName = key.match(/toggle\[(.*?)\]/)[1];
        filters.toggle[fieldName] = value;
      }
    }

    // Clean up empty taxonomy arrays
    Object.keys(filters.taxonomy).forEach((taxName) => {
      if (filters.taxonomy[taxName].length === 0) {
        delete filters.taxonomy[taxName];
      }
    });

    // In modalita' autocomplete non inviare mai il testo di ricerca
    if (this.config.autocomplete) {
      filters.search = "";
    }

    console.log("[Store Locator] Filters:", filters);

    // Update URL with filters
    this.updateURLWithFilters(filters);

    this.loadStores(filters);
  }

  /**
   * Update URL with current filters (without page reload)
   */
  updateURLWithFilters(filters) {
    const url = new URL(window.location);

    // Clear all existing filter params
    const keysToDelete = [];
    url.searchParams.forEach((value, key) => {
      if (
        key === "search" ||
        key === "open_now" ||
        key === "user_lat" ||
        key === "user_lng" ||
        key === "distance" ||
        key.startsWith("taxonomy[") ||
        key.startsWith("toggle[")
      ) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach((key) => url.searchParams.delete(key));

    // Add new filter params
    // Evito parametro search in modalita' autocomplete
    if (!this.config.autocomplete && filters.search) {
      url.searchParams.set("search", filters.search);
    }

    if (filters.open_now) {
      url.searchParams.set("open_now", filters.open_now);
    }

    if (filters.user_lat && filters.user_lng) {
      url.searchParams.set("user_lat", filters.user_lat);
      url.searchParams.set("user_lng", filters.user_lng);
    }

    if (filters.distance) {
      url.searchParams.set("distance", filters.distance);
    }

    // Add taxonomy filters (multiple values)
    if (filters.taxonomy) {
      Object.keys(filters.taxonomy).forEach((taxName) => {
        const values = filters.taxonomy[taxName];
        if (Array.isArray(values)) {
          values.forEach((value) => {
            url.searchParams.append(`taxonomy[${taxName}][]`, value);
          });
        }
      });
    }

    // Add toggle filters
    if (filters.toggle) {
      Object.keys(filters.toggle).forEach((toggleName) => {
        url.searchParams.set(
          `toggle[${toggleName}]`,
          filters.toggle[toggleName],
        );
      });
    }

    // Update URL without page reload
    window.history.replaceState({}, "", url);
    console.log("[Store Locator] URL updated:", url.toString());
  }

  /**
   * Clear URL parameters (remove all filter params)
   */
  clearURLParameters() {
    const url = new URL(window.location);

    // Remove all filter params
    const keysToDelete = [];
    url.searchParams.forEach((value, key) => {
      if (
        key === "search" ||
        key === "open_now" ||
        key === "user_lat" ||
        key === "user_lng" ||
        key === "distance" ||
        key.startsWith("taxonomy[") ||
        key.startsWith("toggle[")
      ) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach((key) => url.searchParams.delete(key));

    // Update URL without page reload
    window.history.replaceState({}, "", url);
    console.log("[Store Locator] URL parameters cleared");
  }

  /**
   * Toggle between list and map view (single mode and twocol on mobile)
   */
  toggleView(view) {
    const listView = this.element.querySelector(".store-locator-list-view");
    const mapView = this.element.querySelector(".store-locator-map-view");
    const buttons = this.element.querySelectorAll("[data-toggle-view]");

    if (view === "list") {
      listView?.classList.remove("d-none");
      listView?.classList.add("active");
      mapView?.classList.add("d-none");
      mapView?.classList.remove("active");
    } else {
      listView?.classList.add("d-none");
      listView?.classList.remove("active");
      mapView?.classList.remove("d-none");
      mapView?.classList.add("active");

      // Initialize map if not already initialized (mobile twocol first time)
      if (!this.isMapLoaded) {
        console.log("[Store Locator] Initializing map on first view");
        this.initMap();
        // Update map with current stores
        this.updateMap();
      } else {
        // Trigger map resize if already initialized
        if (this.map) {
          google.maps.event.trigger(this.map, "resize");
          if (this.stores.length > 0) {
            const bounds = new google.maps.LatLngBounds();
            this.markers.forEach((marker) =>
              bounds.extend(marker.getPosition()),
            );
            this.map.fitBounds(bounds);
          }
        }
      }
    }

    // Update button states
    buttons.forEach((btn) => {
      if (btn.dataset.toggleView === view) {
        btn.classList.add("active");
        btn.classList.remove("btn-outline-primary");
        btn.classList.add("btn-primary");
      } else {
        btn.classList.remove("active");
        btn.classList.remove("btn-primary");
        btn.classList.add("btn-outline-primary");
      }
    });
  }

  /**
   * Show loading overlay
   */
  showLoading() {
    const loading = this.element.querySelector(".store-locator-loading");
    if (loading) {
      loading.style.display = "flex";
    }
  }

  /**
   * Hide loading overlay
   */
  hideLoading() {
    const loading = this.element.querySelector(".store-locator-loading");
    if (loading) {
      loading.style.display = "none";
    }
  }

  /**
   * Show error message
   */
  showError(message) {
    const listContainer = this.element.querySelector("#store-locator-list");
    if (listContainer) {
      listContainer.innerHTML = `<div class="col-12"><div class="alert alert-danger">${message}</div></div>`;
    }
  }

  /**
   * Bind load more events
   */
  bindLoadMoreEvents() {
    const loadMoreBtn = this.element.querySelector(".store-locator-load-more");
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener("click", () => {
        this.handleLoadMore();
      });
    }

    // Setup infinite scroll for desktop twocol view
    if (this.tipo_vista === "twocol") {
      this.setupInfiniteScroll();
    }
  }

  /**
   * Handle load more button click
   */
  handleLoadMore() {
    if (this.isLoading || !this.hasMore) return;

    this.isLoading = true;
    this.currentPage++;

    // Show loading spinner on button
    const loadMoreBtn = this.element.querySelector(".store-locator-load-more");
    const btnText = loadMoreBtn?.querySelector(".load-more-text");
    const btnSpinner = loadMoreBtn?.querySelector(".spinner-border");

    if (btnText) btnText.style.display = "none";
    if (btnSpinner) btnSpinner.style.display = "inline-block";
    if (loadMoreBtn) loadMoreBtn.disabled = true;

    console.log("[Store Locator] Loading page:", this.currentPage);

    // Get current filters
    const filters = this.getCurrentFilters();

    // Load stores
    this.loadStores(filters);
  }

  /**
   * Reset pagination to first page
   */
  resetPagination() {
    console.log("[Store Locator] Reset pagination");
    this.currentPage = 1;
    this.totalPages = 1;
    this.hasMore = false;
    this.stores = [];
  }

  /**
   * Update load more button visibility and state
   */
  updateLoadMoreButton() {
    const loadMoreContainer = this.element.querySelector(
      ".store-locator-load-more-container",
    );
    const loadMoreBtn = this.element.querySelector(".store-locator-load-more");
    const btnText = loadMoreBtn?.querySelector(".load-more-text");
    const btnSpinner = loadMoreBtn?.querySelector(".spinner-border");

    if (loadMoreContainer) {
      if (this.hasMore) {
        // Show button
        loadMoreContainer.style.display = "block";
      } else {
        // Hide button
        loadMoreContainer.style.display = "none";
      }
    }

    // Reset button state
    if (loadMoreBtn) {
      loadMoreBtn.disabled = false;
    }
    if (btnText) {
      btnText.style.display = "inline";
    }
    if (btnSpinner) {
      btnSpinner.style.display = "none";
    }
  }

  /**
   * Get current filters from form
   */
  getCurrentFilters() {
    const filters = {
      search: "",
      taxonomy: {},
      toggle: {},
      open_now: "",
      user_lat: "",
      user_lng: "",
      distance: "",
    };

    const filterForm = this.element.querySelector("#store-locator-filter");
    if (!filterForm) return filters;

    const formData = new FormData(filterForm);

    for (let [key, value] of formData.entries()) {
      if (key === "search") {
        filters.search = value;
      } else if (key === "open_now") {
        filters.open_now = value;
      } else if (key === "user_lat") {
        filters.user_lat = value;
      } else if (key === "user_lng") {
        filters.user_lng = value;
      } else if (key === "distance") {
        filters.distance = value;
      } else if (key.startsWith("taxonomy[")) {
        // Extract taxonomy name (handles both "taxonomy[tax]" and "taxonomy[tax][]")
        const match = key.match(/taxonomy\[(.*?)\]/);
        if (match) {
          const taxName = match[1];

          // Initialize array if not exists
          if (!filters.taxonomy[taxName]) {
            filters.taxonomy[taxName] = [];
          }

          // Add value to array (skip empty values)
          if (value !== "" && value !== "Tutti") {
            filters.taxonomy[taxName].push(value);
          }
        }
      } else if (key.startsWith("toggle[")) {
        const fieldName = key.match(/toggle\[(.*?)\]/)[1];
        filters.toggle[fieldName] = value;
      }
    }

    // Clean up empty taxonomy arrays
    Object.keys(filters.taxonomy).forEach((taxName) => {
      if (filters.taxonomy[taxName].length === 0) {
        delete filters.taxonomy[taxName];
      }
    });

    // In modalita' autocomplete non usiamo search
    if (this.config.autocomplete) {
      filters.search = "";
    }

    return filters;
  }

  /**
   * Setup infinite scroll for desktop twocol view
   */
  setupInfiniteScroll() {
    const sentinel = this.element.querySelector(
      ".store-locator-scroll-sentinel",
    );
    if (!sentinel) return;

    // Check if IntersectionObserver is supported
    if (!("IntersectionObserver" in window)) {
      console.log("[Store Locator] IntersectionObserver not supported");
      return;
    }

    // Create media query for desktop (lg breakpoint = 992px)
    const desktopMediaQuery = window.matchMedia("(min-width: 992px)");

    // Intersection Observer callback
    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Only auto-load on desktop
          if (desktopMediaQuery.matches && this.hasMore && !this.isLoading) {
            console.log("[Store Locator] Infinite scroll triggered");
            this.handleLoadMore();
          }
        }
      });
    };

    // Create observer
    this.scrollObserver = new IntersectionObserver(observerCallback, {
      root: null, // viewport
      rootMargin: "100px", // Load 100px before sentinel is visible
      threshold: 0.1,
    });

    // Start observing
    this.scrollObserver.observe(sentinel);

    console.log("[Store Locator] Infinite scroll initialized");
  }

  /**
   * Show debug message in page
   */
  showDebugMessage(message, type = "info") {
    // Create or get debug container
    let debugContainer = this.element.querySelector(".store-locator-debug");
    if (!debugContainer) {
      debugContainer = document.createElement("div");
      debugContainer.className = "store-locator-debug";
      debugContainer.style.cssText =
        "position: fixed; bottom: 20px; right: 20px; z-index: 9999; max-width: 400px;";
      this.element.appendChild(debugContainer);
    }

    // Add message
    const messageEl = document.createElement("div");
    messageEl.className = `alert alert-${type} alert-dismissible fade show`;
    messageEl.innerHTML = `
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
    `;
    debugContainer.appendChild(messageEl);

    // Auto remove after 5 seconds
    setTimeout(() => {
      messageEl.remove();
    }, 5000);
  }
}

// Initialize all store locators on page
document.addEventListener("DOMContentLoaded", () => {
  console.log("[Store Locator] DOM Ready");
  console.log("[Store Locator] window.siteVars:", window.siteVars);

  // Check if siteVars is available
  if (!window.siteVars || !window.siteVars.ajaxUrl) {
    console.error(
      "[Store Locator] ERRORE: window.siteVars.ajaxUrl non disponibile!",
    );
  }

  const storeLocators = document.querySelectorAll(".block__store-locator");
  console.log("[Store Locator] Blocchi trovati:", storeLocators.length);

  storeLocators.forEach((element, index) => {
    console.log(`[Store Locator] Inizializzazione blocco ${index + 1}`);
    new StoreLocator(element);
  });
});
